import type { ICacheEntry } from '@rpgc-ai/shared-interfaces/interfaces/cache.interface';

/**
 * Класс для управления кешированием данных
 */
export class CacheManager<T> {
  /**
   * Кеш для хранения данных
   */
  private cache = new Map<string, ICacheEntry<T>>();
  
  /**
   * Время жизни кеша в мс
   */
  private cacheTTL: number;

  /**
   * Конструктор
   * @param cacheTTL Время жизни кеша в мс (по умолчанию 5 минут)
   */
  constructor(cacheTTL = 5 * 60 * 1000) {
    this.cacheTTL = cacheTTL;
  }

  /**
   * Получить данные из кеша
   * @param key Ключ
   * @returns Данные или null если нет в кеше или истек срок жизни
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);
    const now = Date.now();

    if (entry && now - entry.timestamp < this.cacheTTL) {
      return entry.data;
    }

    if (entry) {
      this.delete(key); // Удаляем устаревшие данные
    }
    
    return null;
  }

  /**
   * Записать данные в кеш
   * @param key Ключ
   * @param data Данные
   */
  set(key: string, data: T): void {
    this.cache.set(key, {
      timestamp: Date.now(),
      data,
    });
  }

  /**
   * Проверить наличие данных в кеше (с учетом TTL)
   * @param key Ключ
   * @returns true если данные есть и не устарели
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Удалить данные из кеша
   * @param key Ключ
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Очистить весь кеш
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Установить время жизни кеша
   * @param milliseconds Время в миллисекундах
   */
  setTTL(milliseconds: number): void {
    this.cacheTTL = milliseconds;
  }
  
  /**
   * Получить размер кеша (количество элементов)
   * @returns Количество элементов в кеше
   */
  getSize(): number {
    return this.cache.size;
  }
}
